import { useEffect, useMemo } from 'react';
import moment from 'moment';
import { comparableTime } from '@op/utils/time';
import type { AnchorMode } from '../types';
import type { useEventItemPosition } from './useEventItemPosition';

interface UseHighPriorityMarkAreaProps {
  /** 是否选中 */
  selected?: boolean;
  /** 是否有任何事件被选中 */
  hasAnySelected: boolean;
  /** 开始时间 */
  startTime?: string;
  /** 结束时间 */
  endTime?: string;
  /** 时间格式 */
  format: string;
  /** 锚点模式 */
  anchorMode: AnchorMode;
  /** 柱子半宽 */
  halfBarWidth: number;
  /** 最大时间值 */
  maxTimeValue: string;
  /** useEventItemPosition 返回的值 */
  positionHook: ReturnType<typeof useEventItemPosition>;
  /** 图表实例（用于动态获取柱子宽度） */
  myChart?: any;
}

interface UseHighPriorityMarkAreaReturn {
  /** 是否选中 */
  isSelected: boolean;
  /** 是否需要显示为灰色 */
  isDimmed: boolean;
  /** mark area 是否显示 */
  markAreaShow: boolean;
  /** mark area 样式 */
  renderMarkArea: { width: string; left: string; height?: string; top?: string };
  /** mark area 时间文本 */
  markAreaTimes: string;
  /** event line 是否高亮 */
  eventLineShow: boolean;
  /** mark area 宽度（数值） */
  markAreaWidth: number;
  /** 鼠标移入处理 */
  handleMouseEnter: () => void;
  /** 鼠标移出处理 */
  handleMouseLeave: () => void;
}

/**
 * 高优先级事件/事件块的 mark area 逻辑 hook
 * 统一处理选中状态、mark area 显示/隐藏、鼠标交互等逻辑
 */
export function useHighPriorityMarkArea({
  selected,
  hasAnySelected,
  startTime,
  endTime,
  format,
  anchorMode,
  halfBarWidth: propHalfBarWidth,
  maxTimeValue,
  positionHook,
  myChart,
}: UseHighPriorityMarkAreaProps): UseHighPriorityMarkAreaReturn {
  const {
    eventLineRef,
    currentItemPosition,
    renderMarkArea,
    eventLineShow,
    markAreaShow,
    markAreaTimes,
    setMarkAreaState,
    getBarLeftPosition,
    setEventBoxPosition,
  } = positionHook;

  // 是否选中
  const isSelected = useMemo(() => selected === true, [selected]);

  // 是否需要显示为灰色
  const isDimmed = useMemo(
    () => hasAnySelected && !isSelected,
    [hasAnySelected, isSelected],
  );

  /**
   * 获取当前柱子的半宽
   * 优先使用主组件传递的缓存值，避免 Hover 时重复调用 ECharts API
   */
  const getCurrentHalfBarWidth = (): number => {
    // 优先使用主组件缓存的值（通过 props 传递）
    if (propHalfBarWidth > 0) {
      return propHalfBarWidth;
    }
    // 仅在缓存值无效时才调用 ECharts API（降级处理）
    if (!myChart) {
      return 0;
    }
    try {
      const seriesModel = myChart.getModel().getSeriesByName('TopTipsEvent')[0];
      const layoutRect = seriesModel?.getData().getItemLayout(0);
      const barWidth = layoutRect?.width || 0;
      return barWidth / 2;
    } catch {
      return 0;
    }
  };

  /**
   * 计算并显示 mark area
   */
  const showMarkArea = () => {
    // 没传时间就不展示标域
    if (!startTime && !endTime) {
      return;
    }

    // 刷新当前位置（图表可能已重绘），但不触发回调
    setEventBoxPosition({ callback: false });

    // 动态获取当前柱子宽度，避免使用过时的 halfBarWidth
    const halfBarWidth = getCurrentHalfBarWidth();

    const start = startTime ? moment(startTime).format(format) : '';
    const end = endTime ? moment(endTime).format(format) : start;

    // 根据 anchorMode 决定如何获取 startLeft 和 endLeftValue
    // 注意：getBarLeftPosition 返回的是柱子的中心位置
    // markArea 应该从 startTime 柱子的左边缘延伸到 endTime 柱子的右边缘
    let startLeft: number;
    let endLeftValue: number;

    if (anchorMode === 'end') {
      // end 模式：currentItemPosition.current 是 endTime 的位置（柱子中心）
      endLeftValue = currentItemPosition.current + halfBarWidth;

      // 需要计算 startTime 的位置
      const startTimePosition = getBarLeftPosition({ xAxis: start });
      startLeft = startTimePosition.left - halfBarWidth;
    } else {
      // start 模式：currentItemPosition.current 是锚点位置（startTime 柱子中心）
      startLeft = currentItemPosition.current - halfBarWidth;

      // 计算结束时间位置
      if (endTime) {
        const maxThanMaxTime = comparableTime(maxTimeValue, end);
        const realXAxis = maxThanMaxTime ? maxTimeValue : end;
        const endItem = getBarLeftPosition({ xAxis: realXAxis });
        endLeftValue = endItem.left + halfBarWidth;
      } else {
        // 没有结束时间时，宽度为一根柱子的宽度
        endLeftValue = startLeft + halfBarWidth * 2;
      }
    }

    // 标记区域位置始终以 start_time 为起点
    const markLeft = startLeft;
    const width = endLeftValue - startLeft;

    // 单次状态更新，减少重渲染
    setMarkAreaState({
      renderMarkArea: {
        left: `${markLeft}px`,
        width: `${width}px`,
        height: eventLineRef.current?.offsetHeight,
        top: eventLineRef.current?.offsetTop + 4,
      },
      eventLineShow: true,
      markAreaShow: true,
      markAreaTimes: anchorMode === 'end' ? end : start,
    });
  };

  /**
   * 隐藏 mark area（仅在未选中时生效）
   */
  const hideMarkArea = () => {
    if (!isSelected) {
      setMarkAreaState({
        renderMarkArea: { width: '0px', left: '0px' },
        eventLineShow: false,
        markAreaShow: false,
        markAreaTimes: '',
      });
    }
  };

  // 选中状态变化时，自动显示/隐藏 mark area
  useEffect(() => {
    if (isSelected) {
      // 使用 setTimeout 确保 DOM 渲染和图表重绘完成后再计算位置
      const timer = setTimeout(() => {
        showMarkArea();
      }, 50);
      return () => clearTimeout(timer);
    }
    hideMarkArea();
  }, [isSelected]);

  // 鼠标悬停事件处理
  const handleMouseEnter = () => {
    showMarkArea();
  };

  const handleMouseLeave = () => {
    hideMarkArea();
  };

  // 标记区域宽度（用于时间标记位置计算）
  const markAreaWidth = parseFloat(renderMarkArea.width) || 0;

  return {
    isSelected,
    isDimmed,
    markAreaShow,
    renderMarkArea,
    markAreaTimes,
    eventLineShow,
    markAreaWidth,
    handleMouseEnter,
    handleMouseLeave,
  };
}
